import type { AgentToolCall } from './agent-tool-call'

/** Wire shape for a completed query-agent response (consumed by ontology-ui). */
export interface QueryResponse {
  answer: string
  cited_nodes: string[]
  tool_calls: AgentToolCall[]
}

/** Parsed `finalize_answer` tool payload (consumed by ontology-ui). */
export interface FinalQueryToolPayload {
  answer: string
  evidence_status: 'graph_evidence' | 'no_graph_evidence'
  cited_nodes: string[]
}

/**
 * Complete close tags the model may emit. The prompt asks for
 * `<answer_start>…</answer_end>`, which is not well-formed XML, so models
 * routinely "correct" it to the symmetric `</answer_start>`, drop the slash,
 * or fall back to the legacy short close.
 *
 * Every tag here is stripped for display, so every tag here must also end the
 * query-only loop. A closer that display accepts but the loop rejects makes
 * the harness re-ask for an answer it already has, and the model rewrites the
 * whole thing.
 *
 * Ordered longest-first: envelope bodies are matched lazily, so a shorter tag
 * that prefixes a longer one would otherwise cut the body short.
 */
const ANSWER_CLOSE_TAGS = ['</answer_start>', '</answer_end>', '<answer_end>', '</answer>'] as const
const ANSWER_OPEN_TAGS = ['<answer_start>', '<answer>'] as const
const STREAMING_OPEN = '<answer_start>'
const LEGACY_OPEN = '<answer>'

function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const STREAMING_ENVELOPE = new RegExp(
  `${escapeRegExp(STREAMING_OPEN)}\\s*([\\s\\S]*?)\\s*(?:${ANSWER_CLOSE_TAGS.map(escapeRegExp).join('|')})`,
  'gi'
)
const LEGACY_ENVELOPE = /<answer>\s*([\s\S]*?)\s*<\/answer>/gi

/**
 * Last match of `pattern` in `text`, or null. Used so a double-wrapped
 * finalize buffer yields the innermost/final envelope body.
 */
function lastMatch(text: string, pattern: RegExp): RegExpExecArray | null {
  pattern.lastIndex = 0
  let match: RegExpExecArray | null = null
  let next: RegExpExecArray | null
  while ((next = pattern.exec(text)) !== null) {
    match = next
    if (next[0].length === 0) pattern.lastIndex += 1
  }
  return match
}

/**
 * Body after the last case-insensitive occurrence of `openMarker`.
 * Returns null when the opener is absent. Skips false positives where
 * `<answer>` is only a prefix of `<answer_start>`.
 */
function bodyAfterOpen(text: string, openMarker: string): string | null {
  const lower = text.toLowerCase()
  const needle = openMarker.toLowerCase()
  let start = -1
  let from = 0
  while (from <= lower.length - needle.length) {
    const index = lower.indexOf(needle, from)
    if (index < 0) break
    // `<answer>` must not match the leading characters of `<answer_start>`.
    if (openMarker === LEGACY_OPEN && lower.startsWith(STREAMING_OPEN, index)) {
      from = index + 1
      continue
    }
    start = index
    from = index + needle.length
  }
  if (start < 0) return null
  return text.slice(start + openMarker.length).trim()
}

/**
 * Remove residual answer-envelope tags and trailing partial tag fragments so
 * markers never reach the chat UI (including when the model omits the `/`
 * on the close tag, or a closer arrives without a matching open extract).
 */
export function stripAnswerMarkers(text: string): string {
  let result = text
    .replace(/<\/?answer_end>/gi, '')
    .replace(/<\/?answer_start>/gi, '')
    .replace(/<\/?answer>/gi, '')
  result = stripTrailingMarkerFragments(result, [...ANSWER_CLOSE_TAGS, ...ANSWER_OPEN_TAGS])
  return result.trim()
}

function stripTrailingMarkerFragments(text: string, markers: readonly string[]): string {
  let result = text
  let changed = true
  while (changed) {
    changed = false
    const lower = result.toLowerCase()
    for (const marker of markers) {
      for (let length = marker.length; length > 0; length--) {
        const fragment = marker.slice(0, length).toLowerCase()
        if (lower.endsWith(fragment)) {
          result = result.slice(0, -length).trimEnd()
          changed = true
          break
        }
      }
      if (changed) break
    }
  }
  return result
}

/**
 * Ask-mode tool that tells the chat UI to show a "Switch to Agent mode"
 * button. The query-agent runner appends {@link SWITCH_TO_AGENT_MODE_MARKER}
 * to the persisted answer when this tool was called so history stays
 * self-describing. Standalone Ask only — the orchestrated query agent does
 * not mount it.
 */
export const SWITCH_TO_AGENT_MODE_TOOL_NAME = 'switch_to_agent_mode'

/**
 * Marker persisted on an Ask-mode answer when the request needs Agent mode.
 * The chat UI strips it from the rendered markdown and shows a
 * "Switch to Agent mode" button in its place. Written by the runner when
 * {@link SWITCH_TO_AGENT_MODE_TOOL_NAME} was called (or, legacy, by the
 * model itself). Never taught in the Ask prompt — the tool is the signal.
 */
export const SWITCH_TO_AGENT_MODE_MARKER = '[[switch-to-agent-mode]]'

const SWITCH_TO_AGENT_MODE_PATTERN = /\[\[switch-to-agent-mode\]\]/gi

/**
 * Prose the model writes when it forgets the tool and just tells the user
 * to switch. Conservative on purpose: "use Agent mode" (capability talk)
 * does not match; "needs / requires / switch to Agent mode" does.
 */
const AGENT_MODE_REDIRECT_PATTERN =
  /\b(?:switch(?:ing)?(?:\s+(?:the\s+)?(?:chat\s+)?(?:composer|dropdown))?\s+to|need(?:s)?|requir(?:es|ed))\s+agent\s+mode\b/i

export interface AgentModeSwitchExtraction {
  /** Answer text with every marker occurrence removed. */
  answer: string
  /** True when the answer should show the Switch to Agent mode button. */
  switchToAgentMode: boolean
}

/**
 * Detect and strip {@link SWITCH_TO_AGENT_MODE_MARKER} from an answer.
 * Also trims a trailing *partial* marker fragment (e.g. `[[switch-to-ag`)
 * so the token never flashes while the answer is still streaming, and
 * treats redirect prose ("needs Agent mode", "switch to Agent mode") as
 * an invite so a forgotten tool call still surfaces the button.
 */
export function extractAgentModeSwitch(text: string): AgentModeSwitchExtraction {
  SWITCH_TO_AGENT_MODE_PATTERN.lastIndex = 0
  const hasMarker = SWITCH_TO_AGENT_MODE_PATTERN.test(text)
  let answer = hasMarker ? text.replace(SWITCH_TO_AGENT_MODE_PATTERN, '') : text
  answer = stripTrailingMarkerFragments(answer, [SWITCH_TO_AGENT_MODE_MARKER])
  const trimmed = answer.trim()
  return {
    answer: trimmed,
    switchToAgentMode: hasMarker || AGENT_MODE_REDIRECT_PATTERN.test(trimmed)
  }
}

/**
 * Append {@link SWITCH_TO_AGENT_MODE_MARKER} when the Ask tool was called
 * and the answer does not already carry it. Idempotent.
 */
export function appendSwitchToAgentModeMarker(
  answer: string,
  toolCalls: ReadonlyArray<{ name: string }>
): string {
  if (!toolCalls.some((call) => call.name === SWITCH_TO_AGENT_MODE_TOOL_NAME)) {
    return answer
  }
  SWITCH_TO_AGENT_MODE_PATTERN.lastIndex = 0
  if (SWITCH_TO_AGENT_MODE_PATTERN.test(answer)) return answer
  const trimmed = answer.trimEnd()
  if (trimmed.length === 0) return SWITCH_TO_AGENT_MODE_MARKER
  return `${trimmed}\n\n${SWITCH_TO_AGENT_MODE_MARKER}`
}

/**
 * True when `text` contains a properly closed envelope with a non-empty body:
 * `<answer_start>` shut by any of {@link ANSWER_CLOSE_TAGS}, or a legacy
 * `<answer>…</answer>`. Progress notes and an unclosed opener do not count.
 *
 * This is the "the model followed the contract" signal — useful for spotting
 * drift. It is NOT the loop-exit test; see {@link hasAttemptedAnswerEnvelope}.
 */
export function hasCompleteAnswerEnvelope(text: string): boolean {
  const streaming = lastMatch(text, STREAMING_ENVELOPE)
  if (streaming?.[1] !== undefined && stripAnswerMarkers(streaming[1]).length > 0) {
    return true
  }
  const legacy = lastMatch(text, LEGACY_ENVELOPE)
  return legacy?.[1] !== undefined && stripAnswerMarkers(legacy[1]).length > 0
}

/**
 * True when `text` opened an answer envelope and wrote a non-empty body,
 * closed or not. Query-only ends its loop on this.
 *
 * Deliberately looser than {@link hasCompleteAnswerEnvelope}, and it has to
 * be: an unclosed envelope is already what {@link stripAnswerEnvelope} keeps
 * as the answer, so a turn that ends here has produced the text we are going
 * to show and store. A turn that stopped mid-answer — `max_tokens`, or a
 * closer we do not recognise — has nothing left to contribute either, and
 * sending it back for another attempt just makes it rewrite the same answer,
 * which streams to the user a second time.
 *
 * The opener stays mandatory. Without it, a progress note or a plan would end
 * the run, which is the regression the closed-envelope test was introduced to
 * fix.
 */
export function hasAttemptedAnswerEnvelope(text: string): boolean {
  if (hasCompleteAnswerEnvelope(text)) return true
  for (const openMarker of ANSWER_OPEN_TAGS) {
    const body = bodyAfterOpen(text, openMarker)
    if (body !== null && stripAnswerMarkers(body).length > 0) return true
  }
  return false
}

/**
 * Remove model-emitted control sentinels and unmatched citation tokens so
 * they never persist or render. Recovers a markdown cite link whose
 * destination was wrapped in angle brackets. Leaves answer-envelope
 * markers alone — those stay in {@link stripAnswerMarkers} so loop-exit
 * predicates do not change.
 *
 * Each removal takes at most one immediately preceding space so nested
 * markdown lists and fenced blocks keep their indentation.
 */
export function stripModelArtifacts(text: string): string {
  let result = text.replace(/\]\(<cite:([^>]*)>\)/g, '](cite:$1)')
  result = result.replace(/ ?<\|[^|]*\|>/g, '')
  result = result.replace(/ ?<cite:[^>]*>/g, '')
  // Do not nibble `cite:` inside `<cite:…` (complete or truncated) or
  // inside a markdown destination `](cite:…)`.
  result = result.replace(/ ?(?<!\]\(|<)cite:\S+/g, '')
  result = result.replace(/ ?(?:<cite:[^>]*|<\|[^|]*|(?<!\]\(|<)cite:\S*)$/, '')
  return result.trim()
}

/**
 * Normalize a completed query answer for storage/display. The text-shaped
 * finalize path wraps the body in `<answer_start>…</answer_end>` so the UI
 * can route the stream to the chat answer slot; older replies used
 * `<answer>…</answer>`. Extract the envelope body even when the model
 * prepends transition prose before the opening marker, so markers never
 * surface to users. Model sentinels and unmatched cite tokens are stripped
 * after envelope extraction.
 */
export function stripAnswerEnvelope(text: string): string {
  return stripModelArtifacts(stripAnswerEnvelopeInner(text))
}

function stripAnswerEnvelopeInner(text: string): string {
  const streaming = lastMatch(text, STREAMING_ENVELOPE)
  if (streaming?.[1] !== undefined) return stripAnswerMarkers(streaming[1])

  const legacy = lastMatch(text, LEGACY_ENVELOPE)
  if (legacy?.[1] !== undefined) return stripAnswerMarkers(legacy[1])

  const afterStreamingOpen = bodyAfterOpen(text, STREAMING_OPEN)
  if (afterStreamingOpen !== null) return stripAnswerMarkers(afterStreamingOpen)

  const afterLegacyOpen = bodyAfterOpen(text, LEGACY_OPEN)
  if (afterLegacyOpen !== null) return stripAnswerMarkers(afterLegacyOpen)

  return stripAnswerMarkers(text)
}
